<template>
    <aside id="new-issue-form" class="generic-sub-sidebar justify-content-between">
        <div class="header secondary-header">
            <div class="title d-flex flex-row align-items-center">
                <p class="text-16">{{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_1') }}</p>
            </div>
        </div>
        <div class="content">
            <div class="gradient-divider"></div>
            <div class="thumbnail" :style="`background-image: url(${newThumb ? newThumb : data.thumbnail});`" @click="emitChangePosition">
                <div class="overlay"></div>
                <p class="text-12 text-weight-bold text-secondary">{{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_2') }}</p>
            </div>
            <p class="text-10 my-1">
                <i>{{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_message_1') }}</i>
            </p>
            <div class="form-container">
                <b-form-file style="display: none" @input="saveThumb" ref="attachment-thumb" plain></b-form-file>
                <div class="d-flex align-items-center justify-content-center">
                    <div class="alter-thumb" @click="editIssueThumb">
                        {{ $t('overwrite_thumbnail.message') }}
                    </div>
                </div>
                <div v-if="newThumb" class="d-flex align-items-center justify-content-center">
                    <div class="remove-custom-thumb" @click="newThumb = null">
                        {{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_message_2') }}
                    </div>
                </div>
                <div class="sub-section">
                    <ci-text-input
                        required
                        ref="title-input"
                        :disabled="isLoading"
                        v-model="issue.title"
                        :label="$t('Components_Viewer_Sidebar_Issues_NewIssueForm_3')"
                        block
                    />

                    <ci-text-input
                        v-model="issue.text"
                        required
                        :disabled="isLoading"
                        :label="$t('Components_Viewer_Sidebar_Issues_NewIssueForm_4')"
                        textarea
                        block
                    />

                    <ci-date-input :errorHighlight="!isIssueDueDateValid" v-model="issue.dueDate" :label="'Vencimento'" />
                    <div v-if="!isIssueDueDateValid" class="due-date-error">
                        {{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_9') }}
                    </div>
                    <ci-multiple-select2
                        v-model="issue.tags"
                        :value="issue.tags"
                        :disabled="isLoading"
                        :options="groupsTreeSelectData"
                        :label="$t('Components_Viewer_Sidebar_Issues_NewIssueForm_6')"
                    />
                </div>

                <div class="sub-section">
                    <div class="drag-files-container">
                        <CustomFileDropzone
                            v-model="issue.attachments"
                            show-preview
                            multiple
                            accept="pdf, png, jpeg, jpg, gif, raw, image/png, image/jpeg, application/pdf"
                        />
                    </div>
                </div>

                <div class="sub-section">
                    <div class="priority-container">
                        <label class="text-10 text-secondary-3">
                            <span style="font-size: 14px" class="text-secondary">*</span>
                            {{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_7') }}</label
                        >
                        <div class="buttons-container">
                            <button :disabled="isLoading" :class="['low', issue.priority == 'low' && 'active']" @click="() => toggleSelectedPriority('low')">
                                {{ $t('low') }}
                            </button>

                            <button
                                :disabled="isLoading"
                                :class="['medium', issue.priority == 'medium' && 'active']"
                                @click="() => toggleSelectedPriority('medium')"
                            >
                                {{ $t('medium') }}
                            </button>

                            <button :disabled="isLoading" :class="['high', issue.priority == 'high' && 'active']" @click="() => toggleSelectedPriority('high')">
                                {{ $t('high') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="sub-section">
                    <ci-select v-model="issue.cause" :disabled="isLoading" :options="causesTreeSelectData" :label="'Causa'" placeholder="Não selecionado" />

                    <ci-select v-model="issue.type" :disabled="isLoading" :options="typesTreeSelectData" :label="'Tipo'" placeholder="Não selecionado" />
                    <ci-multiple-select2
                        v-model="issue.subjects"
                        :disabled="isLoading"
                        :value="issue.subjects"
                        :options="subjectsTreeSelectData"
                        :label="$t('Components_Viewer_Sidebar_Issues_NewIssueForm_5')"
                    />
                </div>
            </div>
        </div>

        <div class="footer">
            <div class="gradient-divider"></div>

            <div class="buttons-container">
                <ci-button @click="cancel" :disabled="isLoading" pill :text="$t('cancel')" type="gray" />

                <b-tooltip v-if="!isFormValid" target="confirm-button" custom-class="white-tooltip" noninteractive triggers="hover">
                    <p class="text-10 text-weight-bold text-primary-3">
                        {{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_message_3') }} <span class="text-accent">*</span>
                        {{ $t('Components_Viewer_Sidebar_Issues_NewIssueForm_message_4') }}
                    </p>
                </b-tooltip>
                <div id="confirm-button">
                    <ci-button
                        @click="saveIssue"
                        pill
                        :disabled="!isFormValid"
                        :loading="isLoading"
                        :text="$t('Components_Viewer_Sidebar_Issues_NewIssueForm_8')"
                    />
                </div>
            </div>
        </div>
    </aside>
</template>
<script>
// =============
// Components
// =============
import CustomFileDropzone from '@/components/Utils/CustomFileDropzone'

import CINUtils from '@/services/Utils'

// ===========
// JSON data
// ===========
import getIssueOptions from '@/data/getIssueOptions'
import getGroups from '@/data/getGroups'
import { mapGetters } from 'vuex'

/**
 * @param {Object} data - Any data you want to pass to the new issue object. Ex: base64 thumbnail, title, description...
 *
 * @event onRequestChangePosition(issue)
 * @event cancel
 */

export default {
    name: 'NewIssueForm',
    props: {
        data: Object,
    },
    components: {
        CustomFileDropzone,
    },
    computed: {
        ...mapGetters('demo', ['getUser', 'getNextIssueCode']),
        isFormValid: function () {
            return this.issue.title && this.issue.text && this.issue.priority && this.isIssueDueDateValid
        },

        isIssueDueDateValid() {
            if (!this.issue.dueDate) {
                return true
            } else {
                if (this.$moment(this.issue.dueDate, 'YYYY-MM-DD').unix() < this.$moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).unix()) {
                    return false
                } else {
                    return true
                }
            }
        },

        formattedTags() {
            if (this.issue.tags.some((t) => typeof t == 'number')) {
                let newMembers = this.issue.tags.filter((t) => typeof t == 'string')
                let currentGroups = this.issue.tags.filter((t) => typeof t == 'number')

                currentGroups.forEach((groupId) => {
                    let currentGroup = this.groupsTreeSelectData.find((g) => g.id == groupId)

                    newMembers = [...newMembers, ...currentGroup.children.map((child) => child.id)]
                })

                return newMembers
            } else {
                return this.issue.tags
            }
        },
    },
    data() {
        return {
            issue: {
                title: null,
                text: null,
                priority: 'low',
                subjects: [],
                tags: [],
                type: null,
                cause: null,
                dueDate: null,
                attachments: [],
                thumbnail: null,
                attachment_thumbnail: null,
            },

            newThumb: null,
            isLoading: false,

            subjects: [],
            subjectsTreeSelectData: [],

            groups: [],
            groupsTreeSelectData: [],

            causesTreeSelectData: [],
            typesTreeSelectData: [],
        }
    },
    methods: {
        // ================
        // Actions
        // ================
        toggleSelectedPriority(priority) {
            this.issue.priority = priority
        },

        async saveIssue() {
            try {
                this.isLoading = true

                try {
                    let thumbUrl = URL.createObjectURL(this.data.thumbnail)
                    this.data.thumbnail = thumbUrl

                    if (this.newThumb) {
                        let attThumbUrl = URL.createObjectURL(this.newThumb)
                        this.issue.attachment_thumbnail = attThumbUrl
                        this.newThumb = null
                    }
                } catch (Err) {
                    console.warn('[THUMB_UPLOAD_FAILED] Thumb upload has failed', Err)
                    this.issue.thumbnail = null
                } finally {
                    try {
                        let promises = this.issue.attachments.map((att) => {
                        return URL.createObjectURL(att)
                    })

                    let URLs = await Promise.all(promises)
                    let attachments = []

                    if (URLs) {
                        attachments = URLs.map((url, i) => ({
                            name: this.issue.attachments[i].name,
                            url,
                            extension: this.issue.attachments[i].type.split('/')[1],
                        }))
                    }

                    let tagsArray = []
                    getGroups.data.getProject.groups.forEach((group) => {
                        group.members.forEach((member) => {
                            this.issue.tags.forEach((t) => {
                                if (typeof t == 'string' && member.email == t) {
                                    const existentGroup = tagsArray.find((g) => g.id == group.id)

                                    if (existentGroup) {
                                        existentGroup.members.push(member)
                                    } else {
                                        tagsArray.push({id: group.id, members: [member]})
                                    }
                                    
                                } else if(group.id == t) {
                                    tagsArray.push(group)
                                }
                            })
                        })
                    })

                    let newIssue = {
                        id: 0,
                        title: this.issue.title,
                        text: this.issue.text,
                        priority: this.issue.priority,
                        pitch: this.data.coords.pitch,
                        yaw: this.data.coords.yaw,
                        thumbnail: this.data.thumbnail,
                        attachment_thumbnail: this.issue.attachment_thumbnail,
                        subjects: this.issue.subjects,
                        tags: tagsArray,
                        attachments: attachments.length ? attachments : [],
                        status: 'open',
                        code: this.getNextIssueCode,
                        cause: this.issue.cause,
                        type: this.issue.type,
                        due_date: this.issue.dueDate,
                        sector: null,
                        floorplan: {
                            id: 503,
                            name: '10º Pavimento - ED. Residencial',
                            url: 'https://firebasestorage.googleapis.com/v0/b/constructin-15d0f.appspot.com/o/projects%2Ffloorplans%2F503-d85c-1570742200472.jpg?alt=media&token=d85c229a-212f-48da-96df-3a208e903e38',
                        },
                        viewpoint: {
                            name: this.data.viewpoint.name,
                            id: this.data.viewpoint.id
                        },
                        image: {
                            id: this.issue.image_id,
                        },
                        created_at: new Date().toLocaleDateString() + " " + new Date().toTimeString(),
                        events: [],
                        created_by: this.getUser,
                    }
                    this.isLoading = false
                    this.$store.commit('demo/ADD_NEW_ISSUE', newIssue)
                    this.$store.commit('demo/UPDATE_STATUS_ISSUES_COUNT', {
                        status: 'open',
                        issue: { id: newIssue.id },
                    })

                    this.$emit('success', newIssue)
                    }catch(e) {
                        console.log(e)
                    }
                }
            } catch (Err) {
                this.isLoading = false
            }
        },

        // =================
        // Queries
        // =================
        getGroupsQuery() {
            const data = getGroups.data
            this.groups = data.getProject.groups
            this.groupsTreeSelectData = this.groups.map((group) => {
                return {
                    id: group.id,
                    label: group.name ? group.name : this.$t('keyword86'),
                    children: group.members.map((member) => {
                        return {
                            id: member.email,
                            label: member.user ? member.user.name + ' ' + member.user.surname : member.email,
                        }
                    }),
                }
            })
        },

        getIssueOptionsQuery() {
            const data = getIssueOptions.data
            this.subjects = data.getIssueOptions.subjects
            this.causes = data.getIssueOptions.causes
            this.types = data.getIssueOptions.types

            this.subjectsTreeSelectData = this.subjects.map((s) => ({
                id: s.id,
                label: s.name,
            }))

            this.causesTreeSelectData = []
            this.causes.forEach((c) => {
                c.options.forEach((o) => {
                    this.causesTreeSelectData.push({
                        id: o.id,
                        label: o.label + ' - ' + o.name,
                    })
                })
            })

            this.typesTreeSelectData = []
            this.types.forEach((t) => {
                t.options.forEach((o) => {
                    this.typesTreeSelectData.push({
                        id: o.id,
                        label: o.label + ' - ' + o.name,
                    })
                })
            })
        },

        editIssueThumb() {
            this.$refs['attachment-thumb'].$el.click()
        },

        async saveThumb(file) {
            if (file.type.includes('image')) {
                this.newThumb = await CINUtils.toBase64(file)
            }
        },

        // ===================
        // Events
        // ===================
        emitChangePosition() {
            this.$emit('onRequestChangePosition', this.issue)
        },
        cancel() {
            this.$emit('cancel')
        },
    },
    mounted() {
        this.getIssueOptionsQuery()
        this.getGroupsQuery()
        this.$refs['title-input'].focus()

        this.issue = {
            ...this.issue,
            ...this.data,
        }
    },
}
</script>
<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/generic-sub-sidebar.scss';
@import '@/scss/components/360Viewer/Sidebar/Issues/new-issue-form.scss';
</style>
